import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";
import { useStaticQuery, graphql } from "gatsby";


const SEO = ({ title, description, image, pathname: suppliedPathname, article, noindex }) => {
  const location = useLocation();
  const { site } = useStaticQuery(query);
  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata;

  const pathname = suppliedPathname || location.pathname || "/";
  const canonicalPath = pathname.startsWith("http")
    ? new URL(pathname).pathname
    : pathname;
  const normalizedCanonicalPath = canonicalPath === "/" || canonicalPath.endsWith("/")
    ? canonicalPath
    : `${canonicalPath}/`;
  const canonicalUrl = new URL(normalizedCanonicalPath, siteUrl).toString();
  const selectedImage = image || defaultImage;
  const imageUrl = selectedImage
    ? (selectedImage.startsWith("http") ? selectedImage : new URL(selectedImage, siteUrl).toString())
    : null;
  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: imageUrl,
    url: canonicalUrl,
  };
  
  return (
    <Helmet title={seo.title} titleTemplate={seo.title === defaultTitle ? undefined : titleTemplate}>
      <link rel="canonical" href={seo.url} />
      <meta name="description" content={seo.description} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {seo.image && <meta name="image" content={seo.image} />}
      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content={article ? "article" : "website"} />
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}
      {seo.image && <meta property="og:image" content={seo.image} />}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterUsername && (
        <meta name="twitter:creator" content={twitterUsername} />
      )}
      {seo.title && <meta name="twitter:title" content={seo.title} />}
      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
    </Helmet>
  )
}

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  pathname: PropTypes.string,
  article: PropTypes.bool,
  noindex: PropTypes.bool,
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  pathname: null,
  article: false,
  noindex: false,
};

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
      }
    }
  }
`;
